import React from "react";
import { Image, isPDF } from "@sonder/ui/source/styled/components/Image";
import { ImageSlide } from "@sonder/ui/source/styled/components/ImageSlide";
import { VideoPlayer } from "@sonder/ui/source/styled/components/VideoPlayer";
import { VideoPlayerSlide } from "@sonder/ui/source/styled/fragments/VideoPlayerSlide";
import { useSegment } from "analytics/segment";
import { TaskAttachment } from "core/queries/GetTaskComments";
import { Resource } from "typings/globalGQLTypes";

interface AttachmentsProps {
  attachments: TaskAttachment[] | null;
}

interface AttachmentProps {
  attachment: TaskAttachment;
  onClick?: (attachment: TaskAttachment, fullScreen: boolean) => void;
}

const renderVideoAttachment = ({ attachment }: AttachmentProps) => {
  const { url } = attachment;
  const posterUrl = `${url.slice(0, url.lastIndexOf("."))}.jpg`;
  return (
    <VideoPlayerSlide key={attachment.id}>
      <VideoPlayer controls poster={posterUrl} src={url} />
    </VideoPlayerSlide>
  );
};

const renderPhotoAttachment = ({ attachment, onClick }: AttachmentProps) => (
  <ImageSlide
    isPDF={isPDF(attachment.url) || undefined}
    key={attachment.id}
    onClick={fullScreen => {
      if (onClick) onClick(attachment, fullScreen);
    }}
  >
    <Image src={attachment.url} />
  </ImageSlide>
);

const renderAttachment = ({ attachment, onClick }: AttachmentProps) => {
  switch (attachment.resourceType) {
    case Resource.video:
      return renderVideoAttachment({ attachment, onClick });
    case Resource.image:
      return renderPhotoAttachment({ attachment, onClick });
    default:
      return null;
  }
};

export const Attachments = ({ attachments }: AttachmentsProps) => {
  const { track } = useSegment();
  const onClick = (attachment: TaskAttachment, fullScreen: boolean) => {
    track({
      action: "click",
      element: fullScreen ? "open_fullscreen" : "close_fullscreen",
      eventName: "attachments",
      props: {
        attachmentId: attachment.id,
        attachableId: attachment.attachableId,
        attachableType: attachment.attachableType,
      },
    });
  };
  const attachmentNodes = (attachments || [])
    .map(attachment => renderAttachment({ attachment, onClick }))
    .filter(attachment => !!attachment);

  if (!attachmentNodes || !attachmentNodes.length) return null;

  return <>{attachmentNodes}</>;
};
